import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeService, setActiveService] = useState(0);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Validate textarea length
    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setFormStatus('error');
      return;
    }

    // Get selected service value
    const serviceSelect = form.querySelector('#service') as HTMLSelectElement;
    const serviceValue = serviceSelect.options[serviceSelect.selectedIndex].text;
    
    // Convert to URL-encoded format
    const urlEncodedData = new URLSearchParams();
    urlEncodedData.append('name', formData.get('name') as string);
    urlEncodedData.append('email', formData.get('email') as string);
    urlEncodedData.append('company', formData.get('company') as string);
    urlEncodedData.append('service', serviceValue);
    urlEncodedData.append('message', message);

    try {
      const response = await fetch('https://readdy.ai/api/form/d5ki92s8iuo4ujd55ucg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const services = [
    {
      title: t('services.consulting.title'),
      description: t('services.consulting.description'),
      icon: 'ri-user-voice-line',
      image: 'https://public.readdy.ai/ai/img_res/c18f43084d397705ee3244f19ccb86f3.jpg'
    },
    {
      title: t('services.portTour.title'),
      description: t('services.portTour.description'),
      icon: 'ri-ship-2-line',
      image: 'https://readdy.ai/api/search-image?query=Modern%20container%20port%20terminal%20with%20organized%20stacks%20of%20shipping%20containers%20and%20cranes%2C%20professional%20industrial%20tour%20perspective%20showing%20port%20operations%2C%20clear%20blue%20sky%20with%20simple%20background%20highlighting%20port%20infrastructure&width=600&height=400&seq=port-tour-102&orientation=landscape'
    },
    {
      title: t('services.seminar.title'),
      description: t('services.seminar.description'),
      icon: 'ri-presentation-line',
      image: 'https://readdy.ai/api/search-image?query=Professional%20business%20seminar%20presentation%20with%20speaker%20presenting%20maritime%20logistics%20content%20to%20engaged%20audience%20in%20modern%20conference%20hall%2C%20bright%20lighting%20with%20simple%20clean%20background%2C%20educational%20corporate%20event%20atmosphere&width=600&height=400&seq=seminar-103&orientation=landscape'
    },
    {
      title: t('services.logisticsInfo.title'),
      description: t('services.logisticsInfo.description'),
      icon: 'ri-information-line',
      image: 'https://readdy.ai/api/search-image?query=Modern%20digital%20dashboard%20displaying%20logistics%20data%20analytics%20and%20shipping%20information%20on%20large%20screens%20in%20professional%20office%20setting%2C%20clean%20technology%20interface%20with%20simple%20background%2C%20data%20visualization%20concept&width=600&height=400&seq=logistics-info-104&orientation=landscape'
    },
    {
      title: t('services.routing.title'),
      description: t('services.routing.description'),
      icon: 'ri-route-line',
      image: 'https://readdy.ai/api/search-image?query=Digital%20world%20map%20showing%20optimized%20shipping%20routes%20with%20glowing%20connection%20lines%20across%20oceans%20and%20continents%2C%20modern%20navigation%20technology%20visualization%20with%20simple%20dark%20background%2C%20global%20logistics%20network%20concept&width=600&height=400&seq=routing-105&orientation=landscape'
    },
    {
      title: t('services.solutions.title'),
      description: t('services.solutions.description'),
      icon: 'ri-lightbulb-line',
      image: 'https://readdy.ai/api/search-image?query=Innovative%20logistics%20technology%20solutions%20with%20automated%20container%20handling%20systems%20in%20modern%20warehouse%20facility%2C%20futuristic%20efficient%20operations%20with%20simple%20clean%20background%2C%20smart%20logistics%20concept&width=600&height=400&seq=solutions-106&orientation=landscape'
    },
    {
      title: t('services.relationship.title'),
      description: t('services.relationship.description'),
      icon: 'ri-team-line',
      image: 'https://readdy.ai/api/search-image?query=Diverse%20business%20professionals%20shaking%20hands%20and%20networking%20at%20maritime%20industry%20event%2C%20professional%20relationship%20building%20atmosphere%20with%20simple%20bright%20background%2C%20corporate%20partnership%20concept&width=600&height=400&seq=relationship-107&orientation=landscape'
    }
  ];

  const solutions = [
    {
      title: t('solutionsSection.visibility.title'),
      description: t('solutionsSection.visibility.description'),
      icon: 'ri-eye-line',
      features: [
        t('solutionsSection.visibility.feature1'),
        t('solutionsSection.visibility.feature2'),
        t('solutionsSection.visibility.feature3')
      ]
    },
    {
      title: t('solutionsSection.costOptimization.title'),
      description: t('solutionsSection.costOptimization.description'),
      icon: 'ri-money-dollar-circle-line',
      features: [
        t('solutionsSection.costOptimization.feature1'),
        t('solutionsSection.costOptimization.feature2'),
        t('solutionsSection.costOptimization.feature3')
      ]
    },
    {
      title: t('solutionsSection.riskManagement.title'),
      description: t('solutionsSection.riskManagement.description'),
      icon: 'ri-shield-check-line',
      features: [
        t('solutionsSection.riskManagement.feature1'),
        t('solutionsSection.riskManagement.feature2'),
        t('solutionsSection.riskManagement.feature3')
      ]
    }
  ];

  const tools = [
    {
      name: t('tools.containerTracking.name'),
      description: t('tools.containerTracking.description'),
      icon: 'ri-map-pin-line',
      color: 'from-teal-500 to-cyan-600'
    },
    {
      name: t('tools.rateCalculator.name'),
      description: t('tools.rateCalculator.description'),
      icon: 'ri-calculator-line',
      color: 'from-orange-500 to-red-600'
    },
    {
      name: t('tools.documentation.name'),
      description: t('tools.documentation.description'),
      icon: 'ri-folder-line',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      name: t('tools.analytics.name'),
      description: t('tools.analytics.description'),
      icon: 'ri-bar-chart-box-line',
      color: 'from-purple-500 to-pink-600'
    }
  ];

  const stats = [
    { number: '500+', label: t('hero.vessels') },
    { number: '120', label: t('hero.countries') },
    { number: '24/7', label: t('hero.support') }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <span className={`text-xl font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>SHIPFAN</span>
            </div>
            
            <div className="hidden lg:flex items-center space-x-8">
              <a href="#home" className={`text-sm font-medium tracking-wider uppercase transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>{t('nav.home')}</a>
              <a href="#services" className={`text-sm font-medium tracking-wider uppercase transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>{t('nav.services')}</a>
              <a href="#solutions" className={`text-sm font-medium tracking-wider uppercase transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>{t('nav.solutions')}</a>
              <a href="#tools" className={`text-sm font-medium tracking-wider uppercase transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>{t('nav.tools')}</a>
              <a href="#about" className={`text-sm font-medium tracking-wider uppercase transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>{t('nav.about')}</a>
              <a href="#contact" className={`text-sm font-medium tracking-wider uppercase transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>{t('nav.contact')}</a>
              
              {/* Language Switcher */}
              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={() => changeLanguage('en')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    i18n.language === 'en'
                      ? 'bg-teal-600 text-white'
                      : isScrolled
                      ? 'text-gray-700 hover:bg-gray-100'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  EN
                </button>
                <button
                  onClick={() => changeLanguage('ja')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    i18n.language === 'ja'
                      ? 'bg-teal-600 text-white'
                      : isScrolled
                      ? 'text-gray-700 hover:bg-gray-100'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  日本語
                </button>
              </div>
            </div>

            <div className="hidden lg:block">
              <a href="#contact" className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-3 rounded-full text-sm font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer">{t('nav.requestQuote')}</a>
            </div>

            <button onClick={() => setShowMobileMenu(!showMobileMenu)} className="lg:hidden">
              <i className={`${showMobileMenu ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${isScrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>
        </div>

        {showMobileMenu && (
          <div className="lg:hidden bg-white border-t">
            <div className="px-6 py-4 space-y-3">
              <a href="#home" className="block text-gray-700 hover:text-teal-600 font-medium cursor-pointer" onClick={() => setShowMobileMenu(false)}>{t('nav.home')}</a>
              <a href="#services" className="block text-gray-700 hover:text-teal-600 font-medium cursor-pointer" onClick={() => setShowMobileMenu(false)}>{t('nav.services')}</a>
              <a href="#solutions" className="block text-gray-700 hover:text-teal-600 font-medium cursor-pointer" onClick={() => setShowMobileMenu(false)}>{t('nav.solutions')}</a>
              <a href="#tools" className="block text-gray-700 hover:text-teal-600 font-medium cursor-pointer" onClick={() => setShowMobileMenu(false)}>{t('nav.tools')}</a>
              <a href="#about" className="block text-gray-700 hover:text-teal-600 font-medium cursor-pointer" onClick={() => setShowMobileMenu(false)}>{t('nav.about')}</a>
              <a href="#contact" className="block text-gray-700 hover:text-teal-600 font-medium cursor-pointer" onClick={() => setShowMobileMenu(false)}>{t('nav.contact')}</a>
              
              {/* Mobile Language Switcher */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => { changeLanguage('en'); setShowMobileMenu(false); }}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                    i18n.language === 'en' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  EN
                </button>
                <button
                  onClick={() => { changeLanguage('ja'); setShowMobileMenu(false); }}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                    i18n.language === 'ja' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  日本語
                </button>
              </div>
              
              <a href="#contact" className="block bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-full text-center font-semibold cursor-pointer">{t('nav.requestQuote')}</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img src="https://readdy.ai/api/search-image?query=Massive%20cargo%20container%20ship%20loaded%20with%20hundreds%20of%20colorful%20shipping%20containers%20sailing%20across%20vast%20blue%20ocean%20under%20dramatic%20sky%2C%20aerial%20perspective%20showing%20scale%20and%20power%20of%20maritime%20transportation%2C%20professional%20shipping%20industry%20photography%20with%20simple%20clean%20ocean%20background&width=1920&height=1080&seq=hero-bg-005&orientation=landscape" alt="SHIPFAN Ocean Transportation" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-6xl lg:text-7xl font-serif text-white mb-6 leading-tight">
              {t('hero.title1')}<br />
              {t('hero.title2')}<br />
              {t('hero.title3')}
            </h1>
            <p className="text-lg text-gray-200 mb-8 leading-relaxed max-w-xl">
              {t('hero.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#services" className="bg-white text-gray-900 px-8 py-4 rounded-lg text-base font-semibold hover:bg-gray-100 transition-all text-center whitespace-nowrap cursor-pointer">{t('hero.exploreServices')}</a>
              <a href="#contact" className="border-2 border-white text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-white hover:text-gray-900 transition-all text-center whitespace-nowrap cursor-pointer">{t('hero.getStarted')}</a>
            </div>
          </div>
        </div>

        {/* Floating Stats */}
        <div className="absolute bottom-12 right-12 hidden lg:block">
          <div className="backdrop-blur-xl bg-white/10 rounded-2xl p-6 border border-white/20">
            <div className="flex items-center gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  {index > 0 && <div className="absolute h-12 w-px bg-white/30 -ml-4"></div>}
                  <div className="text-3xl font-bold text-white">{stat.number}</div>
                  <div className="text-sm text-gray-200 mt-1 whitespace-nowrap">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-light text-gray-900 mb-4">{t('services.title')}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('services.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="group cursor-pointer" onClick={() => setActiveService(index)}>
                <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 h-full">
                  <div className="relative h-56 overflow-hidden">
                    <img src={service.image} alt={service.title} className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute top-6 left-6">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                        <i className={`${service.icon} text-2xl text-white`}></i>
                      </div>
                    </div>
                  </div>
                  <div className="p-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed text-sm">{service.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section id="solutions" className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")', backgroundSize: '60px 60px' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="text-orange-400 text-xs font-bold tracking-widest uppercase mb-4">{t('solutionsSection.tag')}</div>
              <h2 className="text-5xl font-serif text-white mb-6 leading-tight">
                {t('solutionsSection.title1')}<br />
                {t('solutionsSection.title2')}<br />
                {t('solutionsSection.title3')}
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed mb-8">
                {t('solutionsSection.description')}
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="#contact" className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer">{t('solutionsSection.scheduleConsultation')}</a>
                <a href="#solutions" className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap cursor-pointer">{t('solutionsSection.learnMore')}</a>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6">
              {solutions.map((solution, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className={`${solution.icon} text-2xl text-white`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2">{solution.title}</h3>
                      <p className="text-gray-300 mb-4">{solution.description}</p>
                      <ul className="space-y-2">
                        {solution.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-gray-200 text-sm">
                            <i className="ri-checkbox-circle-fill text-orange-400"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Tools Section */}
      <section id="tools" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-light text-gray-900 mb-4">{t('tools.title')}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('tools.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {tools.map((tool, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 h-full">
                  <div className={`w-16 h-16 bg-gradient-to-br ${tool.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                    <i className={`${tool.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{tool.name}</h3>
                  <p className="text-gray-600 leading-relaxed">{tool.description}</p>
                  <div className="mt-6">
                    <span className="text-teal-600 font-semibold text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                      {t('tools.tryNow')} <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <a href="#contact" className="inline-block bg-gradient-to-r from-teal-500 to-cyan-600 text-white px-10 py-4 rounded-full font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer">
              {t('tools.requestDemo')}
            </a>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl font-light text-gray-900 mb-6">{t('about.title')}</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                {t('about.description1')}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                {t('about.description2')}
              </p>
            </div>

            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img src="https://readdy.ai/api/search-image?query=Professional%20diverse%20team%20of%20maritime%20logistics%20experts%20in%20modern%20office%20environment%20discussing%20shipping%20operations%20with%20world%20map%20and%20container%20data%20on%20screens%2C%20bright%20corporate%20setting%20with%20simple%20clean%20background%2C%20business%20teamwork%20concept&width=800&height=900&seq=about-team-006&orientation=portrait" alt="SHIPFAN Team" className="w-full h-full object-cover object-top" />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-gradient-to-br from-orange-500 to-red-500 text-white p-8 rounded-2xl shadow-xl max-w-xs">
                <div className="text-sm font-semibold mb-2">{t('about.mission')}</div>
                <p className="text-sm leading-relaxed">
                  {t('about.missionText')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-5xl font-light text-gray-900 mb-6">{t('contact.title')}</h2>
              <p className="text-lg text-gray-600 mb-8">
                {t('contact.description')}
              </p>

              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">{t('contact.email')}</div>
                    <a href="mailto:shipfan1@gmail.com" className="text-gray-600 hover:text-teal-600 cursor-pointer">shipfan1@gmail.com</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">{t('contact.phone')}</div>
                    <a href="tel:+13109721488" className="text-gray-600 hover:text-teal-600 cursor-pointer">1-310-972-1488</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">{t('contact.office')}</div>
                    <p className="text-gray-600">Torrance, CA 90505</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <a href="#" className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-teal-600 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
                <a href="#" className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-teal-600 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-twitter-fill text-xl"></i>
                </a>
                <a href="#" className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-teal-600 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="#" className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-teal-600 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
              </div>
            </div>

            <div className="bg-gray-50 rounded-3xl p-8 lg:p-10">
              <form data-readdy-form id="contact-form" className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">{t('contact.form.fullName')}</label>
                  <input type="text" id="name" name="name" required className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all" placeholder={t('contact.form.fullNamePlaceholder')} />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">{t('contact.form.emailAddress')}</label>
                  <input type="email" id="email" name="email" required className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all" placeholder={t('contact.form.emailPlaceholder')} />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-gray-900 mb-2">{t('contact.form.company')}</label>
                  <input type="text" id="company" name="company" className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all" placeholder={t('contact.form.companyPlaceholder')} />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-semibold text-gray-900 mb-2">{t('contact.form.serviceInterest')}</label>
                  <select id="service" name="service" className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all cursor-pointer">
                    <option value="">{t('contact.form.selectService')}</option>
                    <option value="consultinging">{t('services.consulting.title')}</option>
                    <option value="port-tour">{t('services.portTour.title')}</option>
                    <option value="seminar">{t('services.seminar.title')}</option>
                    <option value="logistics-info">{t('services.logisticsInfo.title')}</option>
                    <option value="routing">{t('services.routing.title')}</option>
                    <option value="solutions">{t('services.solutions.title')}</option>
                    <option value="relationship">{t('services.relationship.title')}</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">{t('contact.form.message')}</label>
                  <textarea id="message" name="message" rows={4} maxLength={500} required className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none" placeholder={t('contact.form.messagePlaceholder')}></textarea>
                  <div className="text-xs text-gray-500 mt-1">{t('contact.form.maxCharacters')}</div>
                </div>

                {formStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                    {t('contact.form.successMessage')}
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                    {t('contact.form.errorMessage')}
                  </div>
                )}

                <button 
                  type="submit" 
                  disabled={formStatus === 'submitting'}
                  className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 text-white px-8 py-4 rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formStatus === 'submitting' ? t('contact.form.sending') : t('contact.form.sendMessage')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-slate-900 to-teal-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
            <div className="lg:col-span-1">
              <div className="flex items-center mb-6">
                <span className="text-lg font-bold">SHIPFAN</span>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed mb-6">
                {t('footer.tagline')}
              </p>
              <div className="flex gap-3">
                <a href="#" className="w-10 h-10 border border-white/30 rounded-full flex items-center justify-center hover:bg-white hover:text-gray-900 transition-all cursor-pointer">
                  <i className="ri-linkedin-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-white/30 rounded-full flex items-center justify-center hover:bg-white hover:text-gray-900 transition-all cursor-pointer">
                  <i className="ri-twitter-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-white/30 rounded-full flex items-center justify-center hover:bg-white hover:text-gray-900 transition-all cursor-pointer">
                  <i className="ri-instagram-line"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-white/30 rounded-full flex items-center justify-center hover:bg-white hover:text-gray-900 transition-all cursor-pointer">
                  <i className="ri-youtube-fill"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-bold tracking-wider uppercase mb-4">{t('footer.services')}</h4>
              <ul className="space-y-3">
                <li><a href="#services" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">{t('services.consulting.title')}</a></li>
                <li><a href="#services" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">{t('services.portTour.title')}</a></li>
                <li><a href="#services" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">{t('services.seminar.title')}</a></li>
                <li><a href="#services" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">{t('services.logisticsInfo.title')}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold tracking-wider uppercase mb-4">{t('footer.solutions')}</h4>
              <ul className="space-y-3">
                <li><a href="#solutions" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">{t('solutionsSection.visibility.title')}</a></li>
                <li><a href="#solutions" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">{t('solutionsSection.costOptimization.title')}</a></li>
                <li><a href="#solutions" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">{t('solutionsSection.riskManagement.title')}</a></li>
                <li><a href="#solutions" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">{t('services.consulting.title')}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold tracking-wider uppercase mb-4">{t('footer.company')}</h4>
              <ul className="space-y-3">
                <li><a href="#about" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">{t('nav.about')}</a></li>
                <li><a href="#contact" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">{t('nav.contact')}</a></li>
                <li><a href="#" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">{t('footer.careers')}</a></li>
                <li><a href="#" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">{t('footer.news')}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold tracking-wider uppercase mb-4">{t('footer.resources')}</h4>
              <ul className="space-y-3">
                <li><a href="#tools" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">{t('nav.tools')}</a></li>
                <li><a href="#" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">{t('footer.documentation')}</a></li>
                <li><a href="#" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">{t('footer.support')}</a></li>
                <li><a href="#" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">{t('footer.faq')}</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm">{t('footer.copyright')}</p>
              <div className="flex items-center gap-6">
                <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer">{t('footer.privacyPolicy')}</a>
                <span className="text-gray-600">|</span>
                <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer">{t('footer.termsOfService')}</a>
                <span className="text-gray-600">|</span>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer">{t('footer.websiteBuilder')}</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
